import React, { useState, useEffect } from 'react';
import { FaCalendar, FaTimes } from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore'; // Import updateDoc
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './EditExpenseModal.module.css';
import { useTheme } from '../../contexts/ThemeContext';

// Initial empty state matching the AddExpenseModal structure
const initialFormData = {
    BütçeAdı: '',
    BütçeKaynak: '',
    BütçeSahibi: '',
    BütçeTipi: 'Gider', // Default type to Gider for expense
    BütçeKategorisi: '',
    GiderTipi: '', // This will be selected by the user
    ParaBirimi: 'TRY',
    KDV: '', // Keep KDV if needed for expenses
    BütçeMiktarı: '',
    BütçeTarihi: '',
    BeklenenÖdemeTarihi: '', // Keep if needed
    ÖdemeDurumu: 'Beklemede' // Default status
};

const EditExpenseModal = ({ isOpen, onClose, onSubmit, budgetData }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when budgetData is available or changes
  useEffect(() => {
    if (budgetData) {
        const populatedData = { ...initialFormData };
        for (const key in initialFormData) {
            if (budgetData.hasOwnProperty(key)) {
               populatedData[key] = budgetData[key];
            }
        }
        // Ensure type is set correctly if somehow missing in old data
        populatedData.BütçeTipi = 'Gider'; 
        setFormData(populatedData);
    } else {
      setFormData(initialFormData);
    }
  }, [budgetData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budgetData || !budgetData.id) {
        toast.error("Düzenlenecek gider bulunamadı!");
        return;
    }
    setIsSubmitting(true);

    // Prepare data for update (exclude id, ensure types)
    const { id, createdAt, ...dataToUpdate } = formData;
    dataToUpdate.BütçeTipi = 'Gider'; // Ensure type is Gider
    // GiderTipi comes from the form selection

    const budgetRef = doc(db, 'Budgets', budgetData.id);

    try {
      await updateDoc(budgetRef, dataToUpdate);

      toast.success('Gider başarıyla güncellendi!');
      onSubmit(budgetData.id, dataToUpdate); // Pass ID and updated data back
      onClose();

    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Gider güncellenirken bir hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ESC key handling
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Background scroll handling
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !budgetData) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Gider Düzenle</h2> {/* Changed title */}
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Form fields match AddExpenseModal, values from state */}
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Gider Adı</label>
              <input
                type="text"
                name="BütçeAdı"
                value={formData.BütçeAdı || ''}
                onChange={handleChange}
                className={styles.input}
                required
                placeholder="Gider adını giriniz"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Kaynak</label>
                <select
                  name="BütçeKaynak"
                  value={formData.BütçeKaynak || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Şirket Kasası">Şirket Kasası</option>
                  <option value="Kredi Kartı">Kredi Kartı</option>
                  <option value="Banka Hesabı">Banka Hesabı</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Harcama Yapan</label>
                <select
                  name="BütçeSahibi"
                  value={formData.BütçeSahibi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Personel A">Personel A</option>
                  <option value="Personel B">Personel B</option>
                  <option value="Yönetici">Yönetici</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Gider Tipi</label>
                <select
                  name="GiderTipi"
                  value={formData.GiderTipi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Ofis Malzemesi">Ofis Malzemesi</option>
                  <option value="Seyahat">Seyahat</option>
                  <option value="Yemek">Yemek</option>
                  <option value="Fatura">Fatura</option>
                  <option value="Diğer">Diğer</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Kategori</label>
                <select
                  name="BütçeKategorisi"
                  value={formData.BütçeKategorisi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Operasyonel">Operasyonel</option>
                  <option value="Pazarlama">Pazarlama</option>
                  <option value="İnsan Kaynakları">İnsan Kaynakları</option>
                </select>
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tutar</label>
                <input
                  type="number"
                  name="BütçeMiktarı"
                  value={formData.BütçeMiktarı || ''}
                  onChange={handleChange}
                  className={styles.input}
                  required
                  placeholder="Tutar giriniz"
                  step="0.01"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Para Birimi</label>
                <select
                  name="ParaBirimi"
                  value={formData.ParaBirimi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Harcama Tarihi</label>
                <div className={styles.dateInputWrapper}>
                  <input
                    type="date"
                    name="BütçeTarihi"
                    value={formData.BütçeTarihi || ''}
                    onChange={handleChange}
                    className={styles.input}
                    required
                  />
                  <FaCalendar className={styles.calendarIcon} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Ödeme Durumu</label>
                <select
                  name="ÖdemeDurumu"
                  value={formData.ÖdemeDurumu || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="Ödendi">Ödendi</option>
                  <option value="Beklemede">Beklemede</option>
                  <option value="İptal">İptal</option>
                </select>
              </div>
            </div>

            {/* Retain KDV / BeklenenÖdemeTarihi fields if needed */}

            <div className={styles.buttonGroup}>
              <button type="button" className={`${styles.button} ${styles.cancelButton}`} onClick={onClose}>
                İptal
              </button>
              <button type="submit" className={`${styles.button} ${styles.submitButton}`} disabled={isSubmitting}>
                {isSubmitting ? 'Güncelleniyor...' : 'Gider Güncelle'} {/* Changed text */}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditExpenseModal; 